import type { ReceivedReaction } from '../../hooks/useWheelRoomStream';
import { GESTURE_BY_ID } from './gestures';

/**
 * Float-up reaction emoji rendered as an absolutely-positioned overlay
 * above a seat avatar. Consumers mount this once per seat and pass the
 * full reactions buffer + the seat's id; the component filters to the
 * matching entries and animates each one through a ~1.5s float.
 *
 * <p>The component is presentational + self-cleaning: it renders only
 * reactions whose age (since {@code emittedAtMs}) is still within the
 * animation lifetime, so old entries in the FIFO buffer don't paint
 * invisible nodes. No setState / useEffect needed — React re-renders
 * on every new reaction push (via the parent's hook state update), at
 * which point the filter naturally drops aged-out entries.
 *
 * <p>Producer brief 2026-05-27 — required for ad #3 "Meet the Crew"
 * carousel: each seat avatar in the wheel-match leaderboard needs to
 * feel alive, with the named cast members reacting to wins, podiums,
 * lance hits. The R1.4 bot auto-emit policy drives reaction density
 * server-side; this component is the client-side rendering.
 *
 * <p>Renders the commissioned gesture sprite for the reaction id
 * ({@link GESTURE_BY_ID}); unknown ids are skipped. Untargeted
 * (broadcast / to-the-table) gestures float here over the sender's
 * avatar; directed one-to-one gestures surface in the GestureToast
 * for their recipient instead.
 */
export interface ReactionFloatProps {
  /** Live FIFO buffer of recent reactions from
   *  {@link useWheelRoomStream}. The component filters internally; pass
   *  the whole list rather than pre-filtering at the call site. */
  reactions: ReadonlyArray<ReceivedReaction>;
  /** The seat this overlay attaches to. Only reactions whose
   *  {@code seatId} matches will render. */
  seatId: number;
}

const REACTION_ANIM_MS = 1500;
/** Small slack added to the visibility window so a stalled animation
 *  frame doesn't pop a half-faded glyph back into view. */
const REACTION_VISIBILITY_SLACK_MS = 250;

export function ReactionFloat({ reactions, seatId }: ReactionFloatProps) {
  // Reactions arrive in arbitrary order; filter both by seat (only
  // OUR avatar's floats) and by age (skip ones older than the
  // animation lifetime — they're either already faded or were missed
  // entirely because the user backgrounded the tab). The buffer is
  // capped at ~10 entries so this filter is O(buffer-size) per render.
  const now = Date.now();
  const fresh: ReceivedReaction[] = [];
  for (const r of reactions) {
    // Float over the gesture's SUBJECT: the recipient for a directed
    // one-to-one gesture (so the sender sees it land on the target's
    // avatar for a moment), or the sender for a broadcast (to-the-table)
    // gesture. Directed gestures ALSO drive the recipient's GestureToast.
    const subjectSeat = r.targetSeatId != null ? r.targetSeatId : r.seatId;
    if (subjectSeat !== seatId) continue;
    if (now - r.emittedAtMs > REACTION_ANIM_MS + REACTION_VISIBILITY_SLACK_MS) continue;
    fresh.push(r);
  }
  if (fresh.length === 0) return null;

  return (
    <>
      {fresh.map((r) => {
        const src = GESTURE_BY_ID[r.reactionId]?.src;
        if (!src) return null;
        return (
          <span
            key={r.clientId}
            aria-hidden
            style={{
              position: 'absolute',
              left: '50%',
              bottom: '50%',
              transform: 'translateX(-50%)',
              lineHeight: 1,
              animation: `seat-reaction-float ${REACTION_ANIM_MS}ms cubic-bezier(0.18, 0.7, 0.32, 1) forwards`,
              pointerEvents: 'none',
              zIndex: 10,
              // The drop-shadow keeps the sprite legible against any seat
              // ring colour — gold (rank 1, self) or dim-white (others).
              filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.55))',
              whiteSpace: 'nowrap',
            }}
          >
            <img
              src={src}
              alt=""
              width={34}
              height={34}
              draggable={false}
              style={{ display: 'block' }}
            />
          </span>
        );
      })}
      <style>{`
        @keyframes seat-reaction-float {
          0%   { opacity: 0; transform: translate(-50%, 0) scale(0.55); }
          15%  { opacity: 1; transform: translate(-50%, -18px) scale(1.18); }
          75%  { opacity: 1; transform: translate(-50%, -54px) scale(1.0); }
          100% { opacity: 0; transform: translate(-50%, -82px) scale(0.92); }
        }
      `}</style>
    </>
  );
}
